/**
 * Módulos do painel da equipe e o que cada admin pode fazer em cada um.
 *
 * O dono vê e altera tudo e é o único que gerencia usuários. Os demais têm,
 * por módulo, `ver` (só leitura) ou `alterar` (ver e decidir). Módulo ausente =
 * sem acesso. O servidor confere em toda rota; o menu só esconde.
 */
export const MODULOS_ADMIN = [
  'cadastros', 'alteracoes', 'disputas', 'prazos', 'garantias', 'denuncias',
  'suporte', 'parceiros', 'pessoas', 'monetizacao', 'integracoes', 'metricas', 'sugestoes',
] as const;

export type ModuloAdmin = (typeof MODULOS_ADMIN)[number];
export type NivelAdmin = 'ver' | 'alterar';
export type PermissoesAdmin = Partial<Record<ModuloAdmin, NivelAdmin>>;

export const ROTULO_MODULO: Record<ModuloAdmin, string> = {
  cadastros: 'Cadastros',
  alteracoes: 'Alterações de perfil',
  disputas: 'Contestações',
  prazos: 'Prazos',
  garantias: 'Garantias',
  denuncias: 'Denúncias',
  suporte: 'Suporte (atendimentos da Ana e base de conhecimento)',
  parceiros: 'Parceiros',
  pessoas: 'Pessoas (clientes e profissionais; suspender conta)',
  monetizacao: 'Monetização',
  integracoes: 'Integrações',
  metricas: 'Números (funil do profissional e cobertura por cidade)',
  sugestoes: 'Sugestões (quadro de ideias de clientes e profissionais)',
};

/** Quem pode o quê. `papel === 'dono'` passa sempre. */
export function podeAdmin(
  admin: { papel: string; permissoes: PermissoesAdmin },
  modulo: ModuloAdmin,
  nivel: NivelAdmin = 'ver',
): boolean {
  if (admin.papel === 'dono') return true;
  const tem = admin.permissoes[modulo];
  return nivel === 'ver' ? tem === 'ver' || tem === 'alterar' : tem === 'alterar';
}

/** Só módulos e níveis conhecidos: JSON do banco ou do formulário nunca concede o que o código não conhece. */
export function limparPermissoesAdmin(bruto: unknown): PermissoesAdmin {
  const p: PermissoesAdmin = {};
  if (!bruto || typeof bruto !== 'object') return p;
  for (const m of MODULOS_ADMIN) {
    const v = (bruto as Record<string, unknown>)[m];
    if (v === 'ver' || v === 'alterar') p[m] = v;
  }
  return p;
}

/** Tela inicial de cada módulo, na ordem do menu. */
export const INICIO_MODULO: Record<ModuloAdmin, string> = {
  cadastros: '/admin/verificacoes', alteracoes: '/admin/alteracoes', disputas: '/admin/disputas',
  prazos: '/admin/prazos', garantias: '/admin/garantias', denuncias: '/admin/denuncias',
  suporte: '/admin/suporte', parceiros: '/admin/profissionais', pessoas: '/admin/pessoas', monetizacao: '/admin/monetizacao',
  integracoes: '/admin/configuracao', metricas: '/admin/metricas', sugestoes: '/admin/sugestoes',
};

/** Primeira tela que o admin pode abrir; sem nenhum módulo, a própria conta. */
export function inicioAdmin(admin: { papel: string; permissoes: PermissoesAdmin }): string {
  const m = MODULOS_ADMIN.find((x) => podeAdmin(admin, x));
  return m ? INICIO_MODULO[m] : '/admin/conta';
}
